import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { handleApiError, showSuccess } from '../utils/errorHandler';
import { useTranslation } from '../contexts/I18nContext';
import { 
  Building2, Users, Plus, Eye, Trash2, Pencil, UserPlus, Key,
  LogOut, RefreshCw, Play, Pause, ExternalLink,
  Shield, Search, UserCog, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function SuperAdminDashboardPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [newUserDialogOpen, setNewUserDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editFormData, setEditFormData] = useState({});
  const [userFormData, setUserFormData] = useState({
    email: '',
    password: '',
    nome: '',
    cognome: '',
    ruolo: 'user',
    stato: 'attivo'
  });
  
  // Loading states for various actions
  const [savingTenant, setSavingTenant] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [deletingTenant, setDeletingTenant] = useState(null); // tenant id
  const [deletingUser, setDeletingUser] = useState(null); // user id
  const [suspendingTenant, setSuspendingTenant] = useState(null); // tenant id
  const [impersonating, setImpersonating] = useState(null); // user email
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({ email: '', nome: '', cognome: '' });
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Delete confirmation dialog
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, tenant: null, step: 1 });
  
  const [formData, setFormData] = useState({
    nome: '',
    slug: '',
    partita_iva: '',
    codice_fiscale: '',
    indirizzo: '',
    citta: '',
    cap: '',
    provincia: '',
    telefono: '',
    email: '',
    pec: '',
    lingua: 'it',
    admin_email: '',
    admin_password: '',
    admin_nome: '',
    admin_cognome: ''
  });

  const getToken = () => localStorage.getItem('superAdminToken');

  const handleLogout = useCallback(() => {
    localStorage.removeItem('superAdminToken');
    localStorage.removeItem('superAdmin');
    window.location.href = '/admin';
  }, []);

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      handleApiError({ message: 'Le password non coincidono' });
      return;
    }
    if (passwordForm.new_password.length < 8) {
      handleApiError({ message: 'La password deve avere almeno 8 caratteri' });
      return;
    }
    setSavingProfile(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/me/password`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: passwordForm.current_password, new_password: passwordForm.new_password })
      });
      if (response.ok) {
        showSuccess('Password aggiornata con successo');
        setPasswordDialogOpen(false);
        setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      } else {
        const err = await response.json();
        handleApiError({ message: err.detail || 'Errore aggiornamento password' });
      }
    } catch (error) {
      handleApiError({ message: 'Errore di rete' });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleUpdateProfile = async () => {
    setSavingProfile(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/me/profile`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(profileForm)
      });
      if (response.ok) {
        const data = await response.json();
        setAdmin(data.admin);
        showSuccess('Profilo aggiornato');
        setProfileDialogOpen(false);
      } else {
        const err = await response.json();
        handleApiError({ message: err.detail || 'Errore aggiornamento profilo' });
      }
    } catch (error) {
      handleApiError({ message: 'Errore di rete' });
    } finally {
      setSavingProfile(false);
    }
  };

  const openProfileDialog = () => {
    setProfileForm({ email: admin?.email || '', nome: admin?.nome || '', cognome: admin?.cognome || '' });
    setProfileDialogOpen(true);
  };

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/dashboard`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        setDashboard(await response.json());
      } else if (response.status === 401) {
        handleLogout();
      }
    } catch (error) {
      handleApiError({ message: 'Caricamento dati dashboard non riuscito' });
    } finally {
      setLoading(false);
    }
  }, [handleLogout]);

  useEffect(() => {
    const adminData = localStorage.getItem('superAdmin');
    if (adminData) {
      setAdmin(JSON.parse(adminData));
    }
    fetchDashboard();
  }, [fetchDashboard]);

  const resetForm = () => {
    setFormData({
      nome: '', slug: '', partita_iva: '', codice_fiscale: '',
      indirizzo: '', citta: '', cap: '', provincia: '',
      telefono: '', email: '', pec: '', lingua: 'it',
      admin_email: '', admin_password: '', admin_nome: '', admin_cognome: ''
    });
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    setSavingTenant(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/tenants`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        showSuccess('Azienda creata con successo!');
        setDialogOpen(false);
        resetForm();
        fetchDashboard();
      } else {
        let err; try { err = await response.json(); } catch { err = {}; }
        handleApiError({ message: err.detail || 'Creazione azienda non riuscita' });
      }
    } catch (error) {
      handleApiError({ message: 'Connessione al server non riuscita. Verifica la tua rete.' });
    } finally {
      setSavingTenant(false);
    }
  };

  const handleSuspend = async (tenantId, currentStato) => {
    const action = currentStato === 'attivo' ? 'suspend' : 'activate';
    const confirmMsg = currentStato === 'attivo' 
      ? 'Sospendere questa azienda? Gli utenti non potranno accedere.'
      : 'Riattivare questa azienda?';
    
    if (!window.confirm(confirmMsg)) return;
    
    setSuspendingTenant(tenantId);
    try {
      const response = await fetch(`${API_URL}/api/admin/tenants/${tenantId}/${action}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (response.ok) {
        showSuccess(currentStato === 'attivo' ? 'Azienda sospesa' : 'Azienda riattivata');
        fetchDashboard();
      } else {
        handleApiError({ message: 'Cambio stato azienda non riuscito' });
      }
    } catch (error) {
      handleApiError({ message: 'Connessione al server non riuscita. Verifica la tua rete.' });
    } finally {
      setSuspendingTenant(null);
    }
  };

  // Open delete confirmation dialog (step 1)
  const openDeleteDialog = (tenant) => {
    setDeleteConfirmDialog({ open: true, tenant, step: 1 });
  };

  // Proceed to step 2 of delete confirmation
  const confirmDeleteStep1 = () => {
    setDeleteConfirmDialog(prev => ({ ...prev, step: 2 }));
  };

  // Execute delete
  const executeDelete = async () => {
    const tenant = deleteConfirmDialog.tenant;
    if (!tenant) return;
    
    setDeletingTenant(tenant.id);
    try {
      const token = getToken();
      if (!token) {
        handleApiError({ message: 'Sessione scaduta, effettua nuovamente il login' });
        handleLogout();
        setDeletingTenant(null);
        setDeleteConfirmDialog({ open: false, tenant: null, step: 1 });
        return;
      }
      
      const response = await fetch(`${API_URL}/api/admin/tenants/${tenant.id}`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        showSuccess(`Azienda "${tenant.nome}" eliminata con successo`);
        setDeleteConfirmDialog({ open: false, tenant: null, step: 1 });
        await fetchDashboard();
      } else {
        let err; try { err = await response.json(); } catch { err = {}; }
        handleApiError({ message: err.detail || 'Eliminazione azienda non riuscita' });
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Delete error:', error);
      handleApiError({ message: 'Connessione al server non riuscita. Verifica la tua rete.' });
    } finally {
      setDeletingTenant(null);
    }
  };

  const openEditDialog = (tenant) => {
    setSelectedTenant(tenant);
    setEditFormData({
      nome: tenant.nome || '',
      partita_iva: tenant.partita_iva || '',
      codice_fiscale: tenant.codice_fiscale || '',
      indirizzo: tenant.indirizzo || '',
      citta: tenant.citta || '',
      cap: tenant.cap || '',
      provincia: tenant.provincia || '',
      telefono: tenant.telefono || '',
      email: tenant.email || '',
      pec: tenant.pec || '',
      lingua: tenant.lingua || 'it'
    });
    setEditDialogOpen(true);
  };

  const handleEditTenant = async (e) => {
    e.preventDefault();
    setSavingTenant(true);
    try {
      const token = getToken();
      if (!token) {
        handleApiError({ message: 'Sessione scaduta — effettua nuovamente il login' });
        handleLogout();
        return;
      }

      const response = await fetch(`${API_URL}/api/admin/tenants/${selectedTenant.id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editFormData)
      });
      
      if (response.ok) {
        showSuccess('Azienda modificata con successo!');
        setEditDialogOpen(false);
        fetchDashboard();
      } else {
        let err; try { err = await response.json(); } catch { err = {}; }
        handleApiError({ message: err.detail || 'Modifica azienda non riuscita' });
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Edit error:', error);
      handleApiError({ message: 'Connessione al server non riuscita. Verifica la tua rete.' });
    } finally {
      setSavingTenant(false);
    }
  };

  const openTenantDetail = async (tenant) => {
    setSelectedTenant(tenant);
    setDetailDialogOpen(true);
    setLoadingUsers(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/tenants/${tenant.id}/users`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTenantUsers(data.users);
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to load tenant users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleImpersonate = async (tenant, userEmail) => {
    setImpersonating(userEmail);
    try {
      const response = await fetch(`${API_URL}/api/admin/impersonate`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tenant_slug: tenant.slug, user_email: userEmail })
      });
      
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('tenant', JSON.stringify(data.tenant));
        localStorage.setItem('currentTenant', tenant.slug);
        localStorage.setItem('impersonatedTenant', tenant.slug);
        showSuccess(`Accesso come ${userEmail} in ${tenant.nome}`);
        window.location.href = data.redirect_url;
      } else {
        handleApiError({ message: "Accesso come utente non riuscito — verifica che l'azienda sia attiva" });
        setImpersonating(null);
      }
    } catch (error) {
      handleApiError({ message: 'Connessione al server non riuscita. Verifica la tua rete.' });
      setImpersonating(null);
    }
  };

  // === GESTIONE UTENTI ===
  const openUserEditDialog = (user) => {
    setSelectedUser(user);
    setUserFormData({
      email: user.email || '',
      password: '',
      nome: user.nome || '',
      cognome: user.cognome || '',
      ruolo: user.ruolo || 'user',
      stato: user.stato || 'attivo'
    });
    setUserDialogOpen(true);
  };

  const openNewUserDialog = () => {
    setUserFormData({
      email: '',
      password: '',
      nome: '',
      cognome: '',
      ruolo: 'user',
      stato: 'attivo'
    });
    setNewUserDialogOpen(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setSavingUser(true);
    try {
      const token = getToken();
      if (!token) {
        handleApiError({ message: 'Sessione scaduta — effettua nuovamente il login' });
        handleLogout();
        return;
      }

      // Prepara dati (escludi password se vuota)
      const updateData = { ...userFormData };
      if (!updateData.password) {
        delete updateData.password;
      }

      const response = await fetch(`${API_URL}/api/admin/tenants/${selectedTenant.id}/users/${selectedUser.id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });
      
      if (response.ok) {
        showSuccess('Utente aggiornato con successo!');
        setUserDialogOpen(false);
        // Ricarica lista utenti
        const usersResponse = await fetch(`${API_URL}/api/admin/tenants/${selectedTenant.id}/users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (usersResponse.ok) {
          const data = await usersResponse.json();
          setTenantUsers(data.users);
        }
      } else {
        let err; try { err = await response.json(); } catch { err = {}; }
        handleApiError({ message: err.detail || 'Modifica utente non riuscita' });
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Update user error:', error);
      handleApiError({ message: 'Connessione al server non riuscita. Verifica la tua rete.' });
    } finally {
      setSavingUser(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setSavingUser(true);
    try {
      const token = getToken();
      if (!token) {
        handleApiError({ message: 'Sessione scaduta — effettua nuovamente il login' });
        handleLogout();
        return;
      }

      const response = await fetch(`${API_URL}/api/admin/tenants/${selectedTenant.id}/users`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userFormData)
      });
      
      if (response.ok) {
        showSuccess('Utente creato con successo!');
        setNewUserDialogOpen(false);
        // Ricarica lista utenti
        const usersResponse = await fetch(`${API_URL}/api/admin/tenants/${selectedTenant.id}/users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (usersResponse.ok) {
          const data = await usersResponse.json();
          setTenantUsers(data.users);
        }
        fetchDashboard(); // Aggiorna anche contatori
      } else {
        let err; try { err = await response.json(); } catch { err = {}; }
        handleApiError({ message: err.detail || 'Creazione utente non riuscita' });
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Create user error:', error);
      handleApiError({ message: 'Connessione al server non riuscita. Verifica la tua rete.' });
    } finally {
      setSavingUser(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Eliminare l'utente "${user.email}"?`)) return;
    
    setDeletingUser(user.id);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/admin/tenants/${selectedTenant.id}/users/${user.id}`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        showSuccess('Utente eliminato');
        // Ricarica lista utenti
        const usersResponse = await fetch(`${API_URL}/api/admin/tenants/${selectedTenant.id}/users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (usersResponse.ok) {
          const data = await usersResponse.json();
          setTenantUsers(data.users);
        }
        fetchDashboard();
      } else {
        let err; try { err = await response.json(); } catch { err = {}; }
        handleApiError({ message: err.detail || 'Eliminazione utente non riuscita' });
      }
    } catch (error) {
      handleApiError({ message: 'Connessione al server non riuscita. Verifica la tua rete.' });
    } finally {
      setDeletingUser(null);
    }
  };

  const getStatoBadge = (stato) => {
    const config = {
      'attivo': 'bg-green-100 text-green-800',
      'sospeso': 'bg-yellow-100 text-yellow-800',
      'disattivato': 'bg-red-100 text-red-800'
    };
    return <Badge className={config[stato] || 'bg-gray-100'}>{stato}</Badge>;
  };

  const filteredTenants = dashboard?.tenants?.filter(t => 
    t.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.slug.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-white">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900" data-testid="super-admin-dashboard">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Super Admin</h1>
              <p className="text-sm text-slate-400">Gestione Aziende</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-slate-300 text-sm">
              {admin?.nome} {admin?.cognome}
            </span>
            <Button variant="outline" size="sm" onClick={openProfileDialog} className="border-slate-600 text-slate-300 hover:bg-slate-700" data-testid="admin-profile-btn">
              <UserCog className="h-4 w-4 mr-2" /> Profilo
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPasswordDialogOpen(true)} className="border-slate-600 text-slate-300 hover:bg-slate-700" data-testid="admin-change-password-btn">
              <Key className="h-4 w-4 mr-2" /> Password
            </Button>
            <Button variant="outline" size="sm" onClick={handleLogout} className="border-slate-600 text-slate-300 hover:bg-slate-700">
              <LogOut className="h-4 w-4 mr-2" />{t('common.logout')}</Button>
          </div>
        </div>
      </header>

      <main className="p-6 space-y-6">
        {/* KPI Cards - Solo essenziali */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-xl">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Aziende</p>
                  <p className="text-3xl font-bold text-white">{dashboard?.totale_tenant || 0}</p>
                  <p className="text-xs text-green-400 mt-1">{dashboard?.tenant_attivi || 0} attive</p>
                </div>
                <Building2 className="h-10 w-10 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Utenti Totali</p>
                  <p className="text-3xl font-bold text-white">{dashboard?.totale_utenti || 0}</p>
                </div>
                <Users className="h-10 w-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tenants List */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <CardTitle className="text-white">Aziende</CardTitle>
                <CardDescription className="text-slate-400">Gestisci le aziende</CardDescription>
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <div className="relative flex-1 sm:flex-none">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Cerca..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 bg-slate-700 border-slate-600 text-white w-full sm:w-48"
                  />
                </div>
                <Button onClick={() => setDialogOpen(true)} className="bg-purple-600 hover:bg-purple-700">
                  <Plus className="h-4 w-4 mr-2" />{t('common.new')}</Button>
                <Button variant="outline" onClick={fetchDashboard} className="border-slate-600 text-slate-300">
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-300">Azienda</TableHead>
                  <TableHead className="text-slate-300">Slug</TableHead>
                  <TableHead className="text-slate-300">{t('common.status')}</TableHead>
                  <TableHead className="text-slate-300">Lingua</TableHead>
                  <TableHead className="text-slate-300 text-right">Utenti</TableHead>
                  <TableHead className="text-slate-300 text-right">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTenants.map((tenant) => (
                  <TableRow key={tenant.id} className="border-slate-700 hover:bg-slate-700/50">
                    <TableCell className="text-white font-medium">{tenant.nome}</TableCell>
                    <TableCell className="text-slate-400 font-mono text-sm">{tenant.slug}</TableCell>
                    <TableCell>{getStatoBadge(tenant.stato)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-slate-300 border-slate-600" data-testid={`lingua-badge-${tenant.slug}`}>
                        {{ it: 'IT', hr: 'HR', sl: 'SL' }[tenant.lingua] || 'IT'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-slate-300">{tenant.stats?.utenti || 0}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => openTenantDetail(tenant)} title={t("common.details")}>
                          <Eye className="h-4 w-4 text-slate-400" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(tenant)} title={t("common.edit")}>
                          <Pencil className="h-4 w-4 text-blue-400" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => window.open(`/${tenant.slug}/dashboard`, '_blank')}
                          title={t("common.open")}
                        >
                          <ExternalLink className="h-4 w-4 text-green-400" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleSuspend(tenant.id, tenant.stato)}
                          title={tenant.stato === 'attivo' ? 'Sospendi' : 'Riattiva'}
                          disabled={suspendingTenant === tenant.id}
                        >
                          {suspendingTenant === tenant.id 
                            ? <Loader2 className="h-4 w-4 text-yellow-400 animate-spin" />
                            : tenant.stato === 'attivo' 
                              ? <Pause className="h-4 w-4 text-yellow-400" />
                              : <Play className="h-4 w-4 text-green-400" />
                          }
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => openDeleteDialog(tenant)}
                          title={t("common.delete")}
                          disabled={deletingTenant === tenant.id}
                        >
                          {deletingTenant === tenant.id 
                            ? <Loader2 className="h-4 w-4 text-red-400 animate-spin" />
                            : <Trash2 className="h-4 w-4 text-red-400" />
                          }
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredTenants.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                      {searchTerm ? 'Nessuna azienda trovata' : 'Nessuna azienda. Crea la prima!'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Dialog Nuova Azienda */}
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogContent className="max-w-2xl bg-slate-800 border-slate-700 text-white max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nuova Azienda</DialogTitle>
              <DialogDescription className="text-slate-400">
                Crea una nuova azienda con database dedicato
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateTenant} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Nome Azienda *</Label>
                  <Input
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    required
                    className="bg-slate-700 border-slate-600"
                    placeholder="Rossi S.r.l."
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Slug URL</Label>
                  <Input
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                    placeholder="auto-generato"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Partita IVA</Label>
                  <Input
                    value={formData.partita_iva}
                    onChange={(e) => setFormData({ ...formData, partita_iva: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Codice Fiscale</Label>
                  <Input
                    value={formData.codice_fiscale}
                    onChange={(e) => setFormData({ ...formData, codice_fiscale: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-slate-300">{t('common.address')}</Label>
                <Input
                  value={formData.indirizzo}
                  onChange={(e) => setFormData({ ...formData, indirizzo: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-300">Citta</Label>
                  <Input
                    value={formData.citta}
                    onChange={(e) => setFormData({ ...formData, citta: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">CAP</Label>
                  <Input
                    value={formData.cap}
                    onChange={(e) => setFormData({ ...formData, cap: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Provincia</Label>
                  <Input
                    value={formData.provincia}
                    onChange={(e) => setFormData({ ...formData, provincia: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                    maxLength={2}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">{t('auth.email')}</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">{t('common.phone')}</Label>
                  <Input
                    value={formData.telefono}
                    onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
              </div>
              
              <div className="border-t border-slate-700 pt-4 mt-4">
                <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                  Lingua Interfaccia
                </h3>
                <div>
                  <Select value={formData.lingua || 'it'} onValueChange={v => setFormData({ ...formData, lingua: v })}>
                    <SelectTrigger className="bg-slate-700 border-slate-600" data-testid="create-lingua-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="it">Italiano</SelectItem>
                      <SelectItem value="hr">Hrvatski (Croato)</SelectItem>
                      <SelectItem value="sl">Slovensko (Sloveno)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="border-t border-slate-700 pt-4 mt-4">
                <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                  <UserCog className="h-5 w-5 text-purple-400" />
                  Admin Azienda
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Nome *</Label>
                    <Input
                      value={formData.admin_nome}
                      onChange={(e) => setFormData({ ...formData, admin_nome: e.target.value })}
                      required
                      className="bg-slate-700 border-slate-600"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Cognome *</Label>
                    <Input
                      value={formData.admin_cognome}
                      onChange={(e) => setFormData({ ...formData, admin_cognome: e.target.value })}
                      required
                      className="bg-slate-700 border-slate-600"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <Label className="text-slate-300">Email *</Label>
                    <Input
                      type="email"
                      value={formData.admin_email}
                      onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                      required
                      className="bg-slate-700 border-slate-600"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Password *</Label>
                    <Input
                      type="password"
                      value={formData.admin_password}
                      onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                      required
                      className="bg-slate-700 border-slate-600"
                      minLength={8}
                    />
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-600" disabled={savingTenant}>{t('common.cancel')}</Button>
                <Button type="submit" className="bg-purple-600 hover:bg-purple-700" disabled={savingTenant}>
                  {savingTenant ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('dialogs.creating')}</>
                  ) : (
                    'Crea Azienda'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog Modifica Azienda */}
        <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); }}>
          <DialogContent className="max-w-2xl bg-slate-800 border-slate-700 text-white max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Modifica Azienda</DialogTitle>
              <DialogDescription className="text-slate-400">
                Modifica i dati di {selectedTenant?.nome}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEditTenant} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Nome Azienda *</Label>
                  <Input
                    value={editFormData.nome || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, nome: e.target.value })}
                    required
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Slug URL</Label>
                  <Input
                    value={selectedTenant?.slug || ''}
                    disabled
                    className="bg-slate-600 border-slate-600 text-slate-400"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Partita IVA</Label>
                  <Input
                    value={editFormData.partita_iva || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, partita_iva: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Codice Fiscale</Label>
                  <Input
                    value={editFormData.codice_fiscale || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, codice_fiscale: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-slate-300">{t('common.address')}</Label>
                <Input
                  value={editFormData.indirizzo || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, indirizzo: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-300">Citta</Label>
                  <Input
                    value={editFormData.citta || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, citta: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">CAP</Label>
                  <Input
                    value={editFormData.cap || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, cap: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Provincia</Label>
                  <Input
                    value={editFormData.provincia || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, provincia: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                    maxLength={2}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">{t('auth.email')}</Label>
                  <Input
                    type="email"
                    value={editFormData.email || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">{t('common.phone')}</Label>
                  <Input
                    value={editFormData.telefono || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, telefono: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-300">PEC</Label>
                <Input
                  type="email"
                  value={editFormData.pec || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, pec: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>

              <div>
                <Label className="text-slate-300">Lingua Interfaccia</Label>
                <Select value={editFormData.lingua || 'it'} onValueChange={v => setEditFormData({ ...editFormData, lingua: v })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600" data-testid="edit-lingua-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="it">Italiano</SelectItem>
                    <SelectItem value="hr">Hrvatski (Croato)</SelectItem>
                    <SelectItem value="sl">Slovensko (Sloveno)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="border-slate-600" disabled={savingTenant}>{t('common.cancel')}</Button>
                <Button type="submit" className="bg-purple-600 hover:bg-purple-700" disabled={savingTenant}>
                  {savingTenant ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('common.saving')}</>
                  ) : (
                    'Salva Modifiche'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog Dettaglio Tenant */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="max-w-2xl bg-slate-800 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle>{selectedTenant?.nome}</DialogTitle>
              <DialogDescription className="text-slate-400">
                Dettagli e utenti
              </DialogDescription>
            </DialogHeader>
            {selectedTenant && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-700 rounded-lg p-3">
                    <p className="text-xs text-slate-400">{t('common.status')}</p>
                    {getStatoBadge(selectedTenant.stato)}
                  </div>
                  <div className="bg-slate-700 rounded-lg p-3">
                    <p className="text-xs text-slate-400">URL</p>
                    <p className="font-mono text-sm text-blue-400">/{selectedTenant.slug}/</p>
                  </div>
                </div>
                
                {(selectedTenant.partita_iva || selectedTenant.indirizzo) && (
                  <div className="bg-slate-700 rounded-lg p-3 text-sm">
                    {selectedTenant.partita_iva && <p className="text-slate-300">P.IVA: {selectedTenant.partita_iva}</p>}
                    {selectedTenant.indirizzo && (
                      <p className="text-slate-400">{selectedTenant.indirizzo}, {selectedTenant.cap} {selectedTenant.citta}</p>
                    )}
                  </div>
                )}

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-md font-medium flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Utenti ({tenantUsers.length})
                      {loadingUsers && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
                    </h3>
                    <Button 
                      size="sm" 
                      onClick={openNewUserDialog}
                      className="bg-purple-600 hover:bg-purple-700 h-7 text-xs"
                    >
                      <UserPlus className="h-3 w-3 mr-1" /> Nuovo Utente
                    </Button>
                  </div>
                  <div className="bg-slate-700 rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-600">
                          <TableHead className="text-slate-300 text-sm">{t('common.name')}</TableHead>
                          <TableHead className="text-slate-300 text-sm">{t('auth.email')}</TableHead>
                          <TableHead className="text-slate-300 text-sm">Ruolo</TableHead>
                          <TableHead className="text-slate-300 text-sm">{t('common.status')}</TableHead>
                          <TableHead className="text-slate-300 text-sm text-right">{t('common.actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {loadingUsers ? (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center py-8">
                              <Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto" />
                              <p className="text-slate-500 text-sm mt-2">Caricamento utenti...</p>
                            </TableCell>
                          </TableRow>
                        ) : tenantUsers.map((user) => (
                          <TableRow key={user.id} className="border-slate-600">
                            <TableCell className="text-white text-sm">{user.nome} {user.cognome}</TableCell>
                            <TableCell className="text-slate-300 text-sm">{user.email}</TableCell>
                            <TableCell>
                              <Badge className={user.ruolo === 'admin' ? 'bg-purple-500' : 'bg-slate-500'} style={{fontSize: '11px'}}>
                                {user.ruolo}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className={user.stato === 'attivo' ? 'bg-green-600' : 'bg-red-600'} style={{fontSize: '11px'}}>
                                {user.stato}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex gap-1 justify-end">
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => openUserEditDialog(user)}
                                  title="Modifica credenziali"
                                  className="h-7 w-7 p-0"
                                >
                                  <Key className="h-3 w-3 text-yellow-400" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => handleImpersonate(selectedTenant, user.email)}
                                  title="Accedi come utente"
                                  className="h-7 w-7 p-0"
                                  disabled={impersonating === user.email}
                                >
                                  {impersonating === user.email 
                                    ? <Loader2 className="h-3 w-3 text-blue-400 animate-spin" />
                                    : <ExternalLink className="h-3 w-3 text-blue-400" />
                                  }
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => handleDeleteUser(user)}
                                  title="Elimina utente"
                                  className="h-7 w-7 p-0"
                                  disabled={deletingUser === user.id}
                                >
                                  {deletingUser === user.id 
                                    ? <Loader2 className="h-3 w-3 text-red-400 animate-spin" />
                                    : <Trash2 className="h-3 w-3 text-red-400" />
                                  }
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        {tenantUsers.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center py-4 text-slate-500 text-sm">
                              Nessun utente
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Dialog Modifica Utente */}
        <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
          <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Key className="h-5 w-5 text-yellow-400" />
                Modifica Utente
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Modifica le credenziali di {selectedUser?.email}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">{t('common.name')}</Label>
                  <Input
                    value={userFormData.nome}
                    onChange={(e) => setUserFormData({ ...userFormData, nome: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">{t('auth.lastName')}</Label>
                  <Input
                    value={userFormData.cognome}
                    onChange={(e) => setUserFormData({ ...userFormData, cognome: e.target.value })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-slate-300">Email *</Label>
                <Input
                  type="email"
                  value={userFormData.email}
                  onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                  required
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              
              <div>
                <Label className="text-slate-300">Nuova Password</Label>
                <Input
                  type="password"
                  value={userFormData.password}
                  onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                  placeholder="Lascia vuoto per non cambiare"
                  minLength={6}
                />
                <p className="text-xs text-slate-500 mt-1">Minimo 6 caratteri. Lascia vuoto per mantenere la password attuale.</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Ruolo</Label>
                  <Select 
                    value={userFormData.ruolo} 
                    onValueChange={(value) => setUserFormData({ ...userFormData, ruolo: value })}
                  >
                    <SelectTrigger className="bg-slate-700 border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">{t('common.status')}</Label>
                  <Select 
                    value={userFormData.stato} 
                    onValueChange={(value) => setUserFormData({ ...userFormData, stato: value })}
                  >
                    <SelectTrigger className="bg-slate-700 border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      <SelectItem value="attivo">{t('common.active')}</SelectItem>
                      <SelectItem value="sospeso">{t('common.suspended')}</SelectItem>
                      <SelectItem value="disattivato">Disattivato</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setUserDialogOpen(false)} className="border-slate-600" disabled={savingUser}>{t('common.cancel')}</Button>
                <Button type="submit" className="bg-yellow-600 hover:bg-yellow-700" disabled={savingUser}>
                  {savingUser ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('common.saving')}</>
                  ) : (
                    'Salva Modifiche'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog Nuovo Utente */}
        <Dialog open={newUserDialogOpen} onOpenChange={setNewUserDialogOpen}>
          <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-green-400" />
                Nuovo Utente
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Crea un nuovo utente per {selectedTenant?.nome}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Nome *</Label>
                  <Input
                    value={userFormData.nome}
                    onChange={(e) => setUserFormData({ ...userFormData, nome: e.target.value })}
                    required
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Cognome *</Label>
                  <Input
                    value={userFormData.cognome}
                    onChange={(e) => setUserFormData({ ...userFormData, cognome: e.target.value })}
                    required
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-slate-300">Email *</Label>
                <Input
                  type="email"
                  value={userFormData.email}
                  onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                  required
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              
              <div>
                <Label className="text-slate-300">Password *</Label>
                <Input
                  type="password"
                  value={userFormData.password}
                  onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                  required
                  className="bg-slate-700 border-slate-600"
                  minLength={6}
                />
                <p className="text-xs text-slate-500 mt-1">Minimo 6 caratteri</p>
              </div>
              
              <div>
                <Label className="text-slate-300">Ruolo</Label>
                <Select 
                  value={userFormData.ruolo} 
                  onValueChange={(value) => setUserFormData({ ...userFormData, ruolo: value })}
                >
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setNewUserDialogOpen(false)} className="border-slate-600" disabled={savingUser}>{t('common.cancel')}</Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700" disabled={savingUser}>
                  {savingUser ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('dialogs.creating')}</>
                  ) : (
                    'Crea Utente'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog Conferma Eliminazione Azienda - Step 1 & 2 */}
        <Dialog open={deleteConfirmDialog.open} onOpenChange={(open) => !deletingTenant && setDeleteConfirmDialog({ open, tenant: null, step: 1 })}>
          <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-400">
                <Trash2 className="h-5 w-5" />
                {deleteConfirmDialog.step === 1 ? 'Conferma Eliminazione' : 'Ultima Conferma'}
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                {deleteConfirmDialog.step === 1 ? (
                  <>
                    Stai per eliminare l'azienda <strong className="text-white">"{deleteConfirmDialog.tenant?.nome}"</strong>.
                    <br /><br />
                    <span className="text-yellow-400">⚠️ ATTENZIONE:</span> Questa azione eliminerà permanentemente:
                    <ul className="list-disc list-inside mt-2 text-slate-300">
                      <li>Tutti gli utenti dell'azienda</li>
                      <li>Tutte le fatture e movimenti</li>
                      <li>Tutti i dati contabili</li>
                      <li>L'intero database aziendale</li>
                    </ul>
                  </>
                ) : (
                  <>
                    <span className="text-red-400 font-bold">🛑 AZIONE IRREVERSIBILE</span>
                    <br /><br />
                    Sei assolutamente sicuro di voler eliminare <strong className="text-white">"{deleteConfirmDialog.tenant?.nome}"</strong>?
                    <br /><br />
                    <span className="text-red-300">Questa azione NON può essere annullata!</span>
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => setDeleteConfirmDialog({ open: false, tenant: null, step: 1 })} 
                className="border-slate-600"
                disabled={deletingTenant}
              >{t('common.cancel')}</Button>
              {deleteConfirmDialog.step === 1 ? (
                <Button 
                  variant="destructive" 
                  onClick={confirmDeleteStep1}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  Procedi
                </Button>
              ) : (
                <Button 
                  variant="destructive" 
                  onClick={executeDelete}
                  disabled={deletingTenant}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {deletingTenant ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Eliminazione in corso...</>
                  ) : (
                    <><Trash2 className="h-4 w-4 mr-2" /> Elimina Definitivamente</>
                  )}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>

      {/* Dialog Cambio Password */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Cambia Password</DialogTitle>
            <DialogDescription className="text-slate-400">Inserisci la password attuale e la nuova password</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Password Attuale</Label>
              <Input
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                data-testid="current-password-input"
              />
            </div>
            <div>
              <Label className="text-slate-300">Nuova Password</Label>
              <Input
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                data-testid="new-password-input"
              />
            </div>
            <div>
              <Label className="text-slate-300">Conferma Nuova Password</Label>
              <Input
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                data-testid="confirm-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialogOpen(false)} className="border-slate-600 text-slate-300">{t('common.cancel')}</Button>
            <Button onClick={handleChangePassword} disabled={savingProfile} className="bg-purple-600 hover:bg-purple-700" data-testid="save-password-btn">
              {savingProfile ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Salva Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Modifica Profilo */}
      <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Modifica Profilo</DialogTitle>
            <DialogDescription className="text-slate-400">Aggiorna i dati del tuo account super admin</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">{t('auth.email')}</Label>
              <Input
                type="email"
                value={profileForm.email}
                onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                data-testid="profile-email-input"
              />
            </div>
            <div>
              <Label className="text-slate-300">{t('common.name')}</Label>
              <Input
                value={profileForm.nome}
                onChange={(e) => setProfileForm({ ...profileForm, nome: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                data-testid="profile-nome-input"
              />
            </div>
            <div>
              <Label className="text-slate-300">{t('auth.lastName')}</Label>
              <Input
                value={profileForm.cognome}
                onChange={(e) => setProfileForm({ ...profileForm, cognome: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white"
                data-testid="profile-cognome-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProfileDialogOpen(false)} className="border-slate-600 text-slate-300">{t('common.cancel')}</Button>
            <Button onClick={handleUpdateProfile} disabled={savingProfile} className="bg-purple-600 hover:bg-purple-700" data-testid="save-profile-btn">
              {savingProfile ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
